/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MainLayout from "../../../layouts/MainLayout";
import Form from "../../../components/forms/Form";
import {
  userProfileFields,
  userPermissionsFields,
} from "../../../constants/forms/userProfileFields";
// import Select from 'react-select'; // Select is now handled by FieldWithSelectOrAutoComplete
import useLoader from "../../../hooks/useLoader";
import useForm from "../../../hooks/useForm";
import {
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../utils/objectUtils";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import { removeEmptyValues } from "../../../utils/objectUtils";
import PageHeader from "../../../components/PageHeader";
import SectionHeader from "../../../components/forms/SectionHeader";
import SimpleBody from "../../../components/forms/SimpleBody";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useNotification from "../../../hooks/useNotification";
import useRemoteSelectOptions from "../../../hooks/useRemoteSelectOptions";
import useUrlFilters from "../../../hooks/useUrlFilters";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";

const EditUserPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showNotification } = useNotification();

  const {
    get: baseServiceGet,
    getByColumn: baseServiceGetById,
    put: baseServicePut,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);

  const { showLoader, hideLoader } = useLoader();
  const queryClient = useQueryClient();

  const { filters, pagination } = useUrlFilters(
    {
      id: [],
      name: [],
      username: [],
      email: [],
      deleted_at: false,
    },
    "users",
  );

  const { formData, handleChange, formatData } = useForm(
    setDefaultFieldValues(userProfileFields),
  );
  const {
    formData: permissionsFormData,
    handleChange: permissionsHandleChange,
    setFormData: setPermissionsFormData,
  } = useForm(
    setDefaultFieldValues(userPermissionsFields),
    userPermissionsFields,
  );

  // --- Queries para dados iniciais (user, roles, permissions, userPermissions) ---

  const {
    data: userData,
    isLoading: isLoadingUser,
    isError: isErrorUser,
    error: userError,
  } = useQueryWithLoader({
    queryKey: ["userDetails", id],
    queryFn: async () => {
      const response = await baseServiceGetById(entities.users.getById(id));
      return response.result;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });

  const {
    data: userPermissionsData,
    isLoading: isLoadingUserPermissions,
    isError: isErrorUserPermissions,
    error: userPermissionsError,
  } = useQueryWithLoader({
    queryKey: ["userPermissions", id],
    queryFn: async () => {
      const response = await baseServiceGet(entities.users.permissions.get(id));
      return response.result || [];
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });

  // Hook para o select de Cargos
  const rolesSelect = useRemoteSelectOptions({
    queryKey: ["roles"],
    fieldId: "roles",
    // * Usando o fetcher pois por padrão existe um filtro para deleted_at
    // * mas a tabela de roles não possui essa coluna
    fetcher: async () => {
      const response = await baseServiceGet(entities.roles.get);
      return response.result;
    },
    formData: permissionsFormData,
    setFormData: setPermissionsFormData,
    isMulti: true,
    mapToOption: (role) => ({ value: role?.id || "", label: role?.name || "" }),
  });

  const selectedRoleIds =
    rolesSelect.selectedValue?.map((role) => role.value) ?? [];

  const { data: rolePermissionsData = [] } = useQueryWithLoader({
    queryKey: ["rolePermissions", selectedRoleIds],
    queryFn: async () => {
      const responses = await Promise.all(
        selectedRoleIds.map((roleId) =>
          baseServiceGet(entities.roles.permissions.get(roleId)),
        ),
      );

      // Junta permissões de todos os cargos
      return responses.flatMap((response) => response?.result ?? []);
    },
    enabled: selectedRoleIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const selects = {
    roles: rolesSelect,
  };

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId];
      if (!selectConfig) return {};

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      };
    },
    [selects],
  );

  const getOptions = useCallback(
    (fieldId) => selects[fieldId]?.options || [],
    [selects],
  );
  const getSelectedValue = useCallback(
    (fieldId) => selects[fieldId]?.selectedValue || null,
    [selects],
  );

  useEffect(() => {
    if (userData) {
      formatData(userData, userProfileFields);
    }
  }, [userData, formatData]);

  useEffect(() => {
    if (userData?.roles) {
      setPermissionsFormData((prev) => ({
        ...prev,
        roles: userData.roles.map((role) => ({
          value: role.id,
          label: role.name,
        })),
      }));
    }
  }, [userData, setPermissionsFormData]);

  useEffect(() => {
    if (userPermissionsData) {
      if (
        Array.isArray(rolesSelect.selectedValue) &&
        rolesSelect.selectedValue.length > 0 &&
        rolePermissionsData
      ) {
        // Combina permissões do usuário + permissões dos cargos selecionados
        const combinedPermissions = [
          ...userPermissionsData,
          ...rolePermissionsData,
        ];
        const uniquePermissionsMap = new Map();

        combinedPermissions.forEach((perm) => {
          if (perm?.id && !uniquePermissionsMap.has(perm.id)) {
            uniquePermissionsMap.set(perm.id, perm);
          }
        });

        const combinedPermissionsArray = Array.from(
          uniquePermissionsMap.values(),
        );

        setPermissionsFormData((prev) => ({
          ...prev,
          permissions: combinedPermissionsArray.map((p) => ({
            value: p.id,
            label: p.name,
          })),
        }));
      } else {
        // Sem cargos selecionados: mantém apenas as permissões do usuário
        setPermissionsFormData((prev) => ({
          ...prev,
          permissions: userPermissionsData.map((p) => ({
            value: p.id,
            label: p.name,
          })),
        }));
      }
    }
  }, [
    userPermissionsData,
    rolePermissionsData,
    rolesSelect.selectedValue,
    setPermissionsFormData,
  ]);

  useEffect(() => {
    if (isLoadingUser || isLoadingUserPermissions) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [isLoadingUser, isLoadingUserPermissions, showLoader, hideLoader]);

  useEffect(() => {
    if (isErrorUser) {
      showNotification(
        "error",
        `Erro ao carregar dados do usuário: ${
          userError?.response?.data?.message || userError?.message
        }`,
      );
    }
    if (isErrorUserPermissions) {
      showNotification(
        "error",
        `Erro ao carregar permissões do usuário: ${
          userPermissionsError?.response?.data?.message ||
          userPermissionsError?.message
        }`,
      );
    }
  }, [
    isErrorUser,
    userError,
    isErrorUserPermissions,
    userPermissionsError,
    showNotification,
  ]);

  // Mutations

  const updateUserMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await baseServicePut(entities.users.update(id), payload);
      return response;
    },
    onSuccess: (data) => {
      if (data?.status === 200) {
        showNotification(
          "success",
          data.message || "Usuário atualizado com sucesso!",
        );
        navigate("/aluguel/usuarios");
        queryClient.invalidateQueries({ queryKey: ["userDetails", id] });
        queryClient.invalidateQueries({
          queryKey: ["users", filters, pagination],
        });
      }
    },
    onError: (error) => {
      console.error("onError", error);
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const updateUserRolesMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await baseServicePut(
        entities.users.rolesUpdate(id),
        payload,
      );
      return response;
    },
    onSuccess: (data) => {
      if (data?.status === 200) {
        queryClient.invalidateQueries({ queryKey: ["userPermissions", id] });
        queryClient.invalidateQueries({ queryKey: ["userDetails", id] });
        queryClient.invalidateQueries({
          queryKey: ["users", filters, pagination],
        });
      }
    },
    onError: (error) => {
      console.error("onError", error);
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  // Submissões
  const handleSubmit = useCallback(async () => {
    try {
      const formattedData = removeEmptyValues(formData);
      await updateUserMutation.mutateAsync(formattedData);
    } catch (error) {
      console.error("Erro ao enviar usuário:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [formData, updateUserMutation]);

  const handleRoleSubmit = useCallback(async () => {
    try {
      const rolesToSubmit =
        permissionsFormData.roles?.map((role) => role.value) || [];

      await updateUserRolesMutation.mutateAsync({
        roles: rolesToSubmit,
      });
    } catch (error) {
      console.error("Erro ao enviar permissões ou cargos:", {
        status: error.status,
        message: error.message,
      });
    }
  }, [permissionsFormData, updateUserRolesMutation]);

  const handleBack = useCallback(() => {
    navigate("/aluguel/usuarios");
  }, [navigate]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Edição de Usuário"
        showBackButton={true}
        backUrl="/aluguel/usuarios"
      />

      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          initialFormData={formData}
          textSubmit="Atualizar Usuário"
          textLoadingSubmit="Atualizando..."
          handleBack={handleBack}
          isLoading={updateUserMutation.isPending}>
          {() => {
            const renderedSections = new Set();

            return userProfileFields.map((section, index) => {
              const sectionType = section.section.split(" - ")[0];
              const showHeader = !renderedSections.has(sectionType);

              if (showHeader) {
                renderedSections.add(sectionType);
              }

              return (
                <div key={section.section + " " + index}>
                  {showHeader && (
                    <SectionHeader
                      section={section.section}
                      viewTable={false}
                      setViewTable={() => {}}
                      addFieldsInData={() => {}}
                    />
                  )}

                  <SimpleBody
                    fields={section.fields}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={formData}
                    handleFieldChange={handleChange}
                    getOptions={() => {}}
                    getSelectedValue={() => {}}
                    getSelectMeta={() => {}}
                  />
                </div>
              );
            });
          }}
        </Form>

        <Form
          onSubmit={handleRoleSubmit}
          initialFormData={permissionsFormData}
          textSubmit="Atualizar Permissões"
          textLoadingSubmit="Atualizando..."
          handleBack={handleBack}
          isLoading={updateUserRolesMutation.isPending}>
          {() => {
            const renderedSections = new Set();

            return userPermissionsFields.map((section, index) => {
              const sectionType = section.section.split(" - ")[0];
              const showHeader = !renderedSections.has(sectionType);

              if (showHeader) {
                renderedSections.add(sectionType);
              }

              return (
                <div key={section.section + " " + index}>
                  {showHeader && (
                    <SectionHeader
                      section={section.section}
                      viewTable={false}
                      setViewTable={() => {}}
                      addFieldsInData={() => {}}
                    />
                  )}

                  <SimpleBody
                    fields={section.fields}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={permissionsFormData}
                    handleFieldChange={permissionsHandleChange}
                    getOptions={getOptions}
                    getSelectedValue={getSelectedValue}
                    getSelectMeta={getSelectMeta}
                  />
                </div>
              );
            });
          }}
        </Form>
      </div>
    </MainLayout>
  );
};

export default EditUserPage;
